import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowBack, PeopleOutline, PersonAdd } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';
import Layout from '../components/Layout';
import { InviteGuestDialog } from '../components/InviteGuestDialog';
import { GuestList } from '../components/GuestList';
import HousePageBackLink from '../components/HousePageBackLink';

interface User {
  id: string;
  firstName: string;
  lastName: string;
}

interface House {
  id: string;
  name: string;
  host: User;
  coHosts?: User[];
  guests?: User[];
}

const HouseMembersPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [house, setHouse] = useState<House | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchData = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [houseResponse, currentUserResponse] = await Promise.all([
        axios.get(`/houses/${id}`),
        axios.get('/users/me'),
      ]);

      setHouse(houseResponse.data);
      setCurrentUser(currentUserResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('houseDetail.failedFetch'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  if (error || !house) {
    return (
      <Layout>
        <Container maxWidth="lg">
          <Box sx={{ py: 4 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate(id ? `/houses/${id}` : '/')} sx={{ mb: 3 }}>
              {t('houseDetail.backToHouse')}
            </Button>
            <Alert severity="error">{error || t('houseDetail.notFound')}</Alert>
          </Box>
        </Container>
      </Layout>
    );
  }

  const isOwner = currentUser && house.host ? house.host.id === currentUser.id : false;
  const isHost = currentUser
    ? isOwner || Boolean(house.coHosts?.some((member) => member.id === currentUser.id))
    : false;

  return (
    <Layout title={t('houseDetail.layoutTitle', { name: house.name })}>
      <Container maxWidth="lg">
        <HousePageBackLink />

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <PeopleOutline color="primary" />
                  <Typography variant="h4">{t('houseDetail.sections.members')}</Typography>
                </Stack>
                <Typography variant="body1" color="text.secondary">
                  {t('houseDetail.sections.membersDescription')}
                </Typography>
              </Box>

              {isHost && (
                <Button variant="contained" startIcon={<PersonAdd />} onClick={() => setInviteDialogOpen(true)}>
                  {t('houseDetail.inviteGuest')}
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
          <GuestList houseId={id!} isHost={isHost} />
        </Paper>

        {isHost && (
          <InviteGuestDialog
            open={inviteDialogOpen}
            onClose={() => setInviteDialogOpen(false)}
            houseId={id!}
            houseName={house.name}
            onSuccess={fetchData}
          />
        )}
      </Container>
    </Layout>
  );
};

export default HouseMembersPage;